<script setup lang="ts">
/**
 * 组件接收 of Props 定义
 */
const props = defineProps<{
  /** 交易商详情数据，用于在子组件内部生成面包屑。 */
  traderData?: any
  /** 是否为黑平台，由详情页父级统一根据接口字段计算后传入。 */
  isBlackPlatform?: boolean
}>()

const { t } = useAppI18n()
const route = useRoute()
const { getBreadcrumb } = useLsBreadcrumb()

type BreadcrumbSourceItem = {
  route: string
  label?: string
  labelKey?: string
}

/** 当前交易商详情页对应的来源面包屑。 */
const sourceBreadcrumb = ref<BreadcrumbSourceItem | null>(null)

/** 从路由中解析当前详情页缓存 key。 */
const exchangeBreadcrumbKey = computed(() => {
  const slug = route.params.slug

  return Array.isArray(slug) ? slug[0] || '' : String(slug || '')
})

/** 按当前语言解析第二级面包屑文案。 */
const resolveSourceBreadcrumbLabel = (breadcrumb: BreadcrumbSourceItem | null) => {
  if (!breadcrumb) {
    return ''
  }

  if (breadcrumb.labelKey) {
    return t(breadcrumb.labelKey)
  }

  return breadcrumb.label || ''
}

/** 交易商展示名称：英文简称 + 不重复的本地简称。 */
const traderDisplayName = computed(() => {
  const shortName = props.traderData?.basicInfo?.shortName || ''
  const localShortName = props.traderData?.basicInfo?.localShortName || ''

  return localShortName && localShortName !== shortName ? `${shortName} ${localShortName}` : shortName
})

/** 当前页面的面包屑配置。 */
const exchangeBreadcrumbItems = computed(() => {
  const isNormalUltimate = Number(props.traderData?.basicInfo?.ultimateType) === 1
  const sourceColor = isNormalUltimate ? '#7D818A' : 'rgba(255,255,255,0.6)'
  const breadcrumbItems: Array<{ label: string; route?: string; color?: string }> = [
    {
      label: t('82520'),
      route: '/',
      color: sourceColor,
    },
  ]

  if (sourceBreadcrumb.value?.route) {
    breadcrumbItems.push({
      label: resolveSourceBreadcrumbLabel(sourceBreadcrumb.value),
      route: sourceBreadcrumb.value.route,
      color: sourceColor,
    })
  }

  breadcrumbItems.push({
    label: traderDisplayName.value,
    color: isNormalUltimate ? '#292A2E' : '#FFF',
  })

  return breadcrumbItems
})

/** 读取当前交易商详情页的来源面包屑。 */
onMounted(() => {
  sourceBreadcrumb.value = getBreadcrumb(exchangeBreadcrumbKey.value)
})

/**
 * 根据 ultimateType 计算不同的 class 类名
 * 1: 普通旗舰店, 2: 钻石旗舰店, 3: Ultra旗舰店, 4: Lite旗舰店
 */

const heroSubClass = computed(() => {
  const basicInfo = props.traderData?.basicInfo
  const ultimateType = basicInfo?.ultimateType
  // Ultra 有两种场景：有背景图 URL / 无 URL
  if (ultimateType === 3) {
    return basicInfo?.backgroundUrl ? 'exchange-detail-hero-ultra-url' : 'exchange-detail-hero-ultra'
  }

  const classMap: Record<number | string, string> = {
    1: 'exchange-detail-hero-normal',
    2: 'exchange-detail-hero-diamond',
    4: 'exchange-detail-hero-lite',
  }

  const matchedClass = classMap[ultimateType]
  if (matchedClass) {
    return matchedClass
  }

  // 黑平台。
  if (props.isBlackPlatform) {
    return 'exchange-detail-hero-black'
  }

  return 'exchange-detail-hero-base'
})
</script>

<template>
  <section :class="['exchange-detail-hero', heroSubClass]">
    <div class="exchange-detail-container h-[126px]">
      <CommonBreadcrumb :items="exchangeBreadcrumbItems" variant="light" />
    </div>
  </section>
</template>

<style scoped lang="less">
.exchange-detail-hero {
  padding-top: 70px;
}

.exchange-detail-hero-base {
  background: linear-gradient(180deg, #4088d8 0%, #7db8fb 100%);
}

.exchange-detail-hero-normal {
  // 普通旗舰店样式
  position: relative;
  background: linear-gradient(180deg, #ffe07e 0%, #ffedb3 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('https://resources1.interface003.com/web30/2026/06/22/ddc5c2ba-0d88-4370-9d47-39f55f3b8327.png')
      center / cover;
    opacity: 0.6;
  }
}

.exchange-detail-hero-diamond {
  // 钻石旗舰店样式
  background:
    url('https://resources1.interface003.com/web30/2026/06/22/288585df-b277-4744-bc34-a0def6fac474.gif') center / cover,
    linear-gradient(180deg, #06606b 0%, #06606b 100%),
    linear-gradient(180deg, #01444d 0%, #6ba2a9 100%);
}

.exchange-detail-hero-lite {
  // Lite旗舰店样式
  background: linear-gradient(180deg, #142864 0%, #1c2d87 100%);
}

.exchange-detail-hero-ultra {
  // Ultra旗舰店 - 无背景图 URL
  background:
    url('https://resources1.interface003.com/web30/2026/07/13/1966d18a-e75a-43fd-bd6e-b2f17577e242.gif') center / cover,
    #433269;
}

.exchange-detail-hero-ultra-url {
  // Ultra旗舰店 - 有背景图 URL
  background:
    url('https://resources1.interface003.com/web30/2026/07/11/26aab907-0920-400b-a2fb-b9e9872c864a.png') center / cover,
    linear-gradient(180deg, #271947 70%, #271947 100%),
    #433269;
}

.exchange-detail-hero-black {
  // 黑平台样式
  background: linear-gradient(180deg, #000 0%, #282828 100%);
}

.exchange-detail-container {
  width: min(1200px, calc(100vw - 48px));
  margin: 0 auto;
}

@media screen and (max-width: 799px) {
  .exchange-detail-container {
    width: min(1200px, calc(100vw - 32px));
  }
}
</style>
